/**
 * CharaKarakaInfoTable Component
 * Displays Chara Karaka system according to Bhrigu Samhita
 * 7 variable significators ranked by distance from rashi center (15°)
 */

import React from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  Badge
} from '@chakra-ui/react';
import { ChevronUpIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { JatakaResponse } from '../../../../../models/api';
import {
  calculateCharaKarakas,
  CharaKarakaData,
  CharaKarakaAttributes
} from '../data/CharaKarakaTableData';
import { BLACK_ONLY_ASHTAMSHA_COLORS, getAshtamshaPlanetColor } from '../../../../../config/ashtamshaColors';
import {
  TABLE_WRAPPER_STYLES,
  TABLE_CONTAINER_STYLES,
  TABLE_BASE_STYLES,
  TABLE_HEADER_STYLES,
  TABLE_ROW_HOVER_STYLES,
  TABLE_TD_BASE_STYLES,
  TABLE_TD_CENTER_STYLES,
  PLANET_TEXT_STYLES,
  EMPTY_MESSAGE_BOX_STYLES,
  COLUMN_STYLES,
  getRankColor
} from './styles/tableStyles';

export interface CharaKarakaInfoTableProps {
  apiResponse?: JatakaResponse | null;
  calculatedData?: {
    significators: Array<{
      rank: number;
      name: string;
      description: string;
      planet: string;
      planetLongitude: number;
    }>;
    calculationTime: number;
    totalPlanets: number;
  };
}

/**
 * Format degree to display in rashi (0°-30°)
 */
function formatDegreeInRashi(degrees: number): string {
  const deg = Math.floor(degrees);
  const min = Math.floor((degrees - deg) * 60);
  return `${deg}°${min.toString().padStart(2, '0')}'`;
}

/**
 * Get direction icon component for movement to/from center
 */
function getDirectionIcon(isMovingToCenter: boolean): { icon: React.ReactElement; color: string } {
  return isMovingToCenter
    ? { icon: <ChevronUpIcon boxSize={4} />, color: 'green.500' }
    : { icon: <ChevronDownIcon boxSize={4} />, color: 'blue.500' };
}

/**
 * Format bhava numbers as comma-separated string
 */
function formatBhavas(bhavas: number[]): string {
  return bhavas.map(b => `${b}th`).join(', ');
}


export const CharaKarakaInfoTable: React.FC<CharaKarakaInfoTableProps> = ({ apiResponse, calculatedData }) => {
  if (!apiResponse) {
    return (
      <Box {...EMPTY_MESSAGE_BOX_STYLES}>
        <Text>No chart data available</Text>
      </Box>
    );
  }

  // Use calculated data from lazy loading if available, otherwise fallback to synchronous calculation
  const charaKarakaData: CharaKarakaData = calculatedData ?
    {
      charaKarakas: calculatedData.significators.map(sig => ({
        karakaType: sig.name,
        karakaName: sig.description,
        rank: sig.rank,
        planet: {
          name: sig.planet,
          short_name: sig.planet,
          longitude: sig.planetLongitude,
          is_retrograde: false // Placeholder - not available in simple interface
        },
        positionInRashi: sig.planetLongitude % 30,
        distanceFromCenter: Math.abs((sig.planetLongitude % 30) - 15),
        isInFirstHalf: (sig.planetLongitude % 30) < 15,
        isMovingToCenter: (sig.planetLongitude % 30) < 15,
        activeBhavas: [], // Placeholder - not calculated in simplified mode
        interpretation: sig.description
      })),
      atmaKarakaSpecial: null // Placeholder - not calculated in simplified mode
    } :
    calculateCharaKarakas(apiResponse);

  if (!charaKarakaData.charaKarakas.length) {
    return (
      <Box {...EMPTY_MESSAGE_BOX_STYLES}>
        <Text>Unable to calculate Chara Karakas</Text>
      </Box>
    );
  }

  return (
    <Box {...TABLE_WRAPPER_STYLES}>
      {/* Main Chara Karaka Table */}
      <TableContainer {...TABLE_CONTAINER_STYLES}>
        <Table {...TABLE_BASE_STYLES}>
          <Thead {...TABLE_HEADER_STYLES}>
            <Tr>
              <Th {...COLUMN_STYLES.xsmall}>Planet</Th>
              <Th {...COLUMN_STYLES.xsmall}>Part</Th>
              <Th {...COLUMN_STYLES.large}>Karaka</Th>
              <Th {...COLUMN_STYLES.medium}>Position</Th>
              <Th {...COLUMN_STYLES.xsmall}>Distance</Th>
              <Th {...COLUMN_STYLES.xsmall}>Half</Th>
              <Th {...COLUMN_STYLES.xsmall}>Active Bhava</Th>
              <Th {...COLUMN_STYLES.xxlarge}>Interpretation</Th>
            </Tr>
          </Thead>
          <Tbody>
            {charaKarakaData.charaKarakas.map((karaka: CharaKarakaAttributes) => {
              const direction = getDirectionIcon(karaka.isMovingToCenter);

              // Determine planet type for color configuration
              let planetType: 'regular' | 'lagna' | 'hora' | 'chandra' | 'retrograde' = 'regular';

              if (karaka.planet.is_lagna) {
                planetType = 'lagna';
              } else if (karaka.planet.is_hora) {
                planetType = 'hora';
              } else if (karaka.planet.is_chandra) {
                planetType = 'chandra';
              } else if (karaka.planet.is_retrograde) {
                planetType = 'retrograde';
              }

              const colors = getAshtamshaPlanetColor(planetType, BLACK_ONLY_ASHTAMSHA_COLORS);

              return (
                <Tr key={karaka.rank} _hover={TABLE_ROW_HOVER_STYLES}>
                  {/* Planet with color coding - FIRST COLUMN */}
                  <Td {...TABLE_TD_BASE_STYLES}>
                    <Text
                      {...PLANET_TEXT_STYLES}
                      color={colors.color}
                      bg={colors.backgroundColor}
                      px={1}
                      border={colors.borderColor ? `1px solid` : 'none'}
                      borderColor={colors.borderColor}
                    >
                      {karaka.planet.is_retrograde && !karaka.planet.is_lagna
                        ? `(${karaka.planet.short_name})`
                        : karaka.planet.short_name}
                    </Text>
                  </Td>

                  {/* Rank with color coding */}
                  <Td {...TABLE_TD_CENTER_STYLES}>
                      <Text fontSize="xs" lineHeight="1.2" minW="30px" textAlign="center">
                          {karaka.rank}
                      </Text>
                  </Td>

                  {/* Karaka Type */}
                  <Td {...TABLE_TD_BASE_STYLES} fontWeight="normal">{karaka.karakaType}</Td>

                  {/* Position in Rashi */}
                  <Td {...TABLE_TD_CENTER_STYLES}>
                    <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                      <Text fontSize="xs" lineHeight="1.2" minW="45px" textAlign="right">
                        {formatDegreeInRashi(karaka.positionInRashi)}
                      </Text>
                      <Box color={direction.color}>
                        {direction.icon}
                      </Box>
                    </Box>
                  </Td>

                  {/* Distance from Center */}
                  <Td {...TABLE_TD_CENTER_STYLES}>
                    <Text fontSize="xs" color={getRankColor(karaka.rank)} minW="45px" textAlign="center">
                      {formatDegreeInRashi(karaka.distanceFromCenter)}
                    </Text>
                  </Td>

                  {/* Rashi Half */}
                  <Td {...TABLE_TD_CENTER_STYLES}>
                    <Badge
                      colorScheme={karaka.isInFirstHalf ? 'blue' : 'purple'}
                      variant="outline"
                      fontSize="xs"
                    >
                      {karaka.isInFirstHalf ? '1st' : '2nd'}
                    </Badge>
                  </Td>

                  {/* Active Bhava */}
                  <Td {...TABLE_TD_CENTER_STYLES}>
                    <Text fontSize="xs" fontWeight="medium">
                      {formatBhavas(karaka.activeBhavas)}
                    </Text>
                  </Td>

                  {/* Interpretation */}
                  <Td {...TABLE_TD_BASE_STYLES} fontWeight="normal">
                    <Text fontSize="xs" color="gray.700">
                      {karaka.interpretation}
                    </Text>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};